import { useEffect, useState } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Bell, CircleHelp, EyeOff, Home, MessageCircle, Newspaper, Radio, Search, ShieldCheck, User, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Wordmark } from "@/components/Wordmark";
import { Avatar, Spinner } from "@/components/common";
import { useMe } from "@/context/MeContext";
import { api, shareLink } from "@/lib/api";
import AgeGate from "@/pages/AgeGate";

const SearchBox = ({ className = "" }) => {
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState(new URLSearchParams(loc.search).get("q") || "");
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`); }}
      className={`flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 shadow-sm transition-shadow focus-within:ring-2 focus-within:ring-slate-300 ${className}`}>
      <Search size={16} className="shrink-0 text-slate-400" />
      <input data-testid="universal-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people and facts"
        className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400" aria-label="Search Middle" />
    </form>
  );
};

const topLink = ({ isActive }) => `relative px-1 py-2 text-sm font-medium transition-colors ${isActive ? "text-slate-900" : "text-slate-500 hover:text-slate-800"}`;

const DesktopNav = () => {
  return (
  <nav className="hidden items-center gap-6 md:flex">
    <NavLink to="/" end className={topLink} data-testid="nav-home">Feed</NavLink>
    <NavLink to="/facts" className={topLink} data-testid="nav-facts">Facts</NavLink>
    <NavLink to="/live" className={topLink} data-testid="nav-live">Live</NavLink>
    <NavLink to="/how-it-works" className={topLink} data-testid="nav-how-it-works">How it works</NavLink>
  </nav>
  );
};

const IconLink = ({ to, icon: Icon, label, badge, testid }) => (
  <Link to={to} data-testid={testid} aria-label={label} className="relative rounded-full p-2 text-slate-600 transition-colors hover:bg-black/5 hover:text-slate-900">
    <Icon size={20} strokeWidth={1.7} />
    {badge > 0 && <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[hsl(var(--social))] px-1 text-[10px] font-semibold text-white">{badge}</span>}
  </Link>
);

const InviteButton = () => {
  const invite = async () => { if ((await shareLink("/", "invite")) === "copied") toast.success("Link copied. Send it to a friend: it shows what Middle is"); };
  return (
    <button type="button" onClick={invite} data-testid="header-invite-btn" aria-label="Invite a friend" title="Invite a friend"
      className="inline-flex items-center gap-1.5 rounded-full p-2 text-slate-600 transition-colors hover:bg-black/5 hover:text-slate-900 lg:border lg:border-border lg:bg-white lg:px-3.5 lg:py-1.5 lg:text-sm lg:font-medium">
      <UserPlus size={20} strokeWidth={1.7} className="lg:h-4 lg:w-4" /><span className="hidden lg:inline">Invite a friend</span>
    </button>
  );
};

const BottomNav = ({ me }) => {
  const tab = ({ isActive }) => `flex min-w-0 flex-1 flex-col items-center gap-0.5 py-1 text-[10px] font-medium transition-colors ${isActive ? "text-slate-900" : "text-slate-400"}`;
  return (
    <nav data-testid="mobile-bottom-nav" className="fixed bottom-0 left-0 right-0 z-40 flex h-16 items-center border-t border-border bg-white/95 px-2 backdrop-blur-lg md:hidden">
      <NavLink to="/" end className={tab} data-testid="tab-home"><Home size={21} strokeWidth={1.7} />Feed</NavLink>
      <NavLink to="/facts" className={tab} data-testid="tab-facts"><Newspaper size={21} strokeWidth={1.7} />Facts</NavLink>
      <NavLink to="/live" className={tab} data-testid="tab-live"><Radio size={21} strokeWidth={1.7} />Live</NavLink>
      <NavLink to="/profile" className={tab} data-testid="tab-profile"><User size={21} strokeWidth={1.7} />Profile</NavLink>
    </nav>
  );
};

export default function Layout() {
  const { me } = useMe();
  const loc = useLocation();
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    const load = () => me && api.get("/notifications").then((r) => setUnread(r.data.unread)).catch(() => {});
    load();
    window.addEventListener("middle:notifications", load);
    return () => window.removeEventListener("middle:notifications", load);
  }, [me, loc.pathname]);

  return (
    <div className="min-h-screen">
      <header className="fixed left-0 right-0 top-0 z-40 border-b border-border bg-[#FAF8F5]/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-4 px-4 sm:px-6">
          <Wordmark />
          <DesktopNav />
          <SearchBox className="ml-auto hidden max-w-sm flex-1 sm:flex" />
          <div className="ml-auto flex items-center gap-1 sm:ml-0">
            <Link to="/how-it-works" className="rounded-full p-2 text-slate-600 md:hidden" aria-label="How it works" title="How it works" data-testid="header-how-it-works-btn"><CircleHelp size={20} strokeWidth={1.7} /></Link>
            <InviteButton />
            <Link to="/search" className="rounded-full p-2 text-slate-600 sm:hidden" data-testid="mobile-search-btn" aria-label="Search"><Search size={20} strokeWidth={1.7} /></Link>
            <IconLink to="/messages" icon={MessageCircle} label="Messages" testid="nav-messages" />
            {me?.settings?.private_mode && <Link to="/privacy" title="Private mode is on: Middle isn't recording your activity" className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-white transition-opacity hover:opacity-90" data-testid="private-mode-badge"><EyeOff size={12} /><span className="hidden sm:inline">Private</span></Link>}
            <IconLink to="/notifications" icon={Bell} label="Notifications" badge={unread} testid="nav-notifications" />
            {me?.role === "admin" && <IconLink to="/admin" icon={ShieldCheck} label="Admin" testid="nav-admin" />}
            <Link to="/profile" className="ml-1 hidden md:block" data-testid="nav-profile"><Avatar user={me} size={32} /></Link>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 pb-28 pt-24 sm:px-6 md:pb-16 md:pt-28">
        {!me ? <Spinner /> : !me.date_of_birth ? <AgeGate /> : !me.onboarded && loc.pathname !== "/welcome" ? <Navigate to="/welcome" replace /> : <Outlet />}
      </main>
      <BottomNav me={me} />
    </div>
  );
}

export { SearchBox };
